import { WarningCircleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useSubmitWithdrawal, type WithdrawalReceipt } from '@entities/legal';
import { ROUTES } from '@shared/config/routes';
import { Button, StateBlock, TextField } from '@shared/ui';

import { LegalArticle } from './LegalArticle';
import styles from './WithdrawalFormPage.module.css';

interface FormValues {
  consumerName: string;
  contractReference: string;
  contactEmail: string;
}

/**
 * Widerrufsfunktion — § 356a BGB, обязательна с 19.06.2026.
 *
 * Норма описывает не «форму обратной связи», а механизм из двух ступеней:
 *
 * 1. **Abs. 1–2** — кнопка «Vertrag widerrufen» ведёт сюда, здесь человек
 *    указывает имя, сведения для идентификации договора и адрес для
 *    подтверждения. Все три поля обязательны — их перечисляет сама норма;
 * 2. **Abs. 3** — отдельная функция подтверждения «Widerruf bestätigen».
 *    Поэтому форма и отправка разделены: одна кнопка «отправить» второй
 *    ступени не образует;
 * 3. **Abs. 4** — подтверждение получения с датой и временем. Экран показывает
 *    их сразу, письмо приходит следом.
 *
 * **Вход не требуется** и не будет требоваться: право отзыва не может зависеть
 * от доступа к почтовому ящику, куда приходит одноразовый код.
 *
 * Поля «причина отзыва» здесь нет: § 355 Abs. 1 S. 2 BGB — «ohne Angabe von
 * Gründen», и предлагать объясниться значит намекать, что объяснение нужно.
 */
export function WithdrawalFormPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const submit = useSubmitWithdrawal();
  // Вторая ступень § 356a Abs. 3 BGB: сначала данные, затем подтверждение.
  const [review, setReview] = useState<FormValues | null>(null);
  const [receipt, setReceipt] = useState<WithdrawalReceipt | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { consumerName: '', contractReference: '', contactEmail: '' },
  });

  if (receipt) {
    return (
      <LegalArticle title={t('legal.withdrawalForm.done.title')}>
        <StateBlock
          title={t('legal.withdrawalForm.done.title')}
          description={t('legal.withdrawalForm.done.text', {
            received: new Date(receipt.received_at).toLocaleString(locale),
          })}
        />
        <p>{t('legal.withdrawalForm.done.email')}</p>
        {receipt.matched_order ? null : (
          // Договор не найден — заявление всё равно принято: по Abs. 5 срок
          // соблюдён отправкой, а разбор — наша работа, не условие приёма.
          <p>{t('legal.withdrawalForm.done.unmatched')}</p>
        )}
        <p>
          <Link to={ROUTES.withdrawal}>{t('legal.withdrawalForm.done.back')}</Link>
        </p>
      </LegalArticle>
    );
  }

  return (
    <LegalArticle title="Vertrag widerrufen" lead={<p>{t('legal.withdrawalForm.lead')}</p>}>
      {review ? (
        <section className={styles.review} aria-labelledby="withdrawal-review">
          <h2 id="withdrawal-review">{t('legal.withdrawalForm.review.title')}</h2>
          <dl className={styles.summary}>
            <dt>{t('legal.withdrawalForm.name')}</dt>
            <dd>{review.consumerName}</dd>
            <dt>{t('legal.withdrawalForm.reference')}</dt>
            <dd>{review.contractReference}</dd>
            <dt>{t('legal.withdrawalForm.email')}</dt>
            <dd>{review.contactEmail}</dd>
          </dl>

          <p className={styles.declaration}>{t('legal.withdrawalForm.declaration')}</p>

          {submit.isError ? (
            // Ошибка — бордовым с иконкой: цвет не единственный носитель смысла
            // (DESIGN_SYSTEM §2.4).
            <p role="alert" className={styles.error}>
              <WarningCircleIcon size={16} aria-hidden="true" className={styles.errorIcon} />
              <span>{t('legal.withdrawalForm.error')}</span>
            </p>
          ) : null}

          <div className={styles.actions}>
            {/* Вторая ступень § 356a Abs. 3 BGB. Надпись — из самой нормы.
                Единственная красная кнопка экрана (DESIGN_SYSTEM §5.1). */}
            <Button
              disabled={submit.isPending}
              onClick={() => {
                submit.mutate(
                  {
                    consumer_name: review.consumerName,
                    contract_reference: review.contractReference,
                    contact_email: review.contactEmail,
                    locale,
                  },
                  { onSuccess: setReceipt },
                );
              }}
            >
              {submit.isPending
                ? t('legal.withdrawalForm.sending')
                : t('legal.withdrawalForm.confirm')}
            </Button>
            <Button
              variant="secondary"
              disabled={submit.isPending}
              onClick={() => {
                setReview(null);
              }}
            >
              {t('legal.withdrawalForm.edit')}
            </Button>
          </div>
        </section>
      ) : (
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => {
            // `handleSubmit` возвращает промис, а обработчику формы нужен void:
            // необработанный отказ иначе теряется без следа.
            void handleSubmit((values) => {
              setReview(values);
            })(event);
          }}
        >
          <TextField
            label={t('legal.withdrawalForm.name')}
            autoComplete="name"
            error={errors.consumerName ? t('legal.withdrawalForm.required') : undefined}
            {...register('consumerName', { required: true })}
          />
          <TextField
            label={t('legal.withdrawalForm.reference')}
            hint={t('legal.withdrawalForm.referenceHint')}
            error={errors.contractReference ? t('legal.withdrawalForm.required') : undefined}
            {...register('contractReference', { required: true })}
          />
          <TextField
            label={t('legal.withdrawalForm.email')}
            type="email"
            autoComplete="email"
            hint={t('legal.withdrawalForm.emailHint')}
            error={errors.contactEmail ? t('legal.withdrawalForm.emailError') : undefined}
            {...register('contactEmail', { required: true, pattern: /.+@.+\..+/ })}
          />

          <Button type="submit">{t('legal.withdrawalForm.next')}</Button>
        </form>
      )}
    </LegalArticle>
  );
}
